import type { AutomaticCarouselOpts } from '@/components/Cms/AutomaticCarousel';
import { AutomaticCarousel } from '@/components/Cms/AutomaticCarousel';
import { SliderProductsTypeEnum } from '@/components/Cms/SliderProducts';
import type {
  ContentfulPlaceholderFragment,
  ContentfulPlaceholderProductDetailsFragment,
  ContentfulSliderProductsAutomaticFragment,
} from '@/graphql/contentful/api';
import type {
  CmsExtraDataRecord,
  ContentfulData,
  ContentType,
  PageSectionsOpts,
} from '@/integrations/contentful';
import {
  getOptForType,
  getOptsForType,
  renderContentfulSection,
} from '@/integrations/contentful';

import type { KeyFeaturesOpts, ProductDetailSectionOpts } from './components';
import { KeyFeaturesSection, ProductDetailSection } from './components';

export type StrapSectionsOpts = PageSectionsOpts<
  ProductDetailSectionOpts | AutomaticCarouselOpts | KeyFeaturesOpts
>;

export const renderContentfulStrapSection: any = (
  data: ContentfulData,
  sectionOpts: StrapSectionsOpts,
  extraDataRecord: CmsExtraDataRecord,
  draftMode: boolean,
) => {
  const section = renderContentfulSection(
    data,
    sectionOpts,
    extraDataRecord,
    draftMode,
  );
  if (section) {
    return section;
  }

  let props = null;
  let opts = null;
  const typename = data?.__typename as ContentType;

  switch (typename) {
    case 'PlaceholderProductDetails':
      props = data as ContentfulPlaceholderProductDetailsFragment;
      opts = getOptForType<ProductDetailSectionOpts>(
        'PlaceholderProductDetails',
        sectionOpts,
      );
      return opts ? (
        <ProductDetailSection
          data-test-id='ProductDetailSection'
          {...props}
          {...opts}
        />
      ) : null;

    case 'SliderProductsAutomatic':
      props = data as ContentfulSliderProductsAutomaticFragment;
      return renderAutomaticCarousels(draftMode, props, sectionOpts);

    case 'Placeholder':
      props = data as ContentfulPlaceholderFragment;
      return renderPlaceholders(props, sectionOpts);

    default:
      return null;
  }
};

const renderAutomaticCarousels = (
  draftMode: boolean,
  props: ContentfulSliderProductsAutomaticFragment,
  sectionOpts?: StrapSectionsOpts,
) => {
  let opts = null;

  if (
    props.type === SliderProductsTypeEnum.YouMayAlsoLike ||
    props.type === SliderProductsTypeEnum.RecentlyViewed
  ) {
    opts = getOptsForType<AutomaticCarouselOpts>(
      'SliderProductsAutomatic',
      sectionOpts,
    ).find((opt) => opt.opts?.type === props.type)?.opts;
    return opts ? (
      <AutomaticCarousel
        data-test-id='AutomaticCarousel'
        {...props}
        {...opts}
        draftMode={draftMode}
      />
    ) : null;
  }

  return null;
};

const renderPlaceholders = (
  props: ContentfulPlaceholderFragment,
  sectionOpts?: StrapSectionsOpts,
) => {
  let opts = null;

  if (props.type === 'key features') {
    opts = getOptsForType<KeyFeaturesOpts>('Placeholder', sectionOpts).find(
      (opt) => opt.opts?.type === 'key features',
    )?.opts;
    return opts ? (
      <KeyFeaturesSection
        data-test-id='KeyFeaturesSection'
        {...props}
        {...opts}
      />
    ) : null;
  }

  return null;
};
